// IndexedDB 永続化。
// .fmavatar の元 zip バイト列をそのまま保存し、再訪時に再パースして復元する
// （パース結果は TypedArray が多く構造化クローンのコストが高いため、原本保持が単純で堅い）。

const DB_NAME = "facemaker";
const DB_VERSION = 1;
const STORE_AVATARS = "avatars";
const STORE_PROJECTS = "projects";
const STORE_META = "meta";

export interface StoredAvatar {
  id: string;
  name: string;
  /** .fmavatar の生 zip バイト列 */
  zip: Uint8Array;
  savedAt: string;
  /** グルーピング/引き継ぎ用キー。Unity側のAvatar Descriptor由来のUID。manifestに無い場合はnull */
  avatarUid: string | null;
  /** zip バイト列の SHA-256（16進）。完全に同一のファイルかどうかの判定に使う */
  hash: string;
  /** 一覧表示用サムネイル（PNG dataURL、128x128程度）。生成失敗時は無い場合がある */
  thumbnail?: string;
}

/** zip バイト列から SHA-256 ハッシュ（16進文字列）を計算する */
export async function hashBytes(bytes: Uint8Array): Promise<string> {
  const copy = new Uint8Array(bytes); // ArrayBufferLike の型不一致を避けるためコピーする
  const digest = await crypto.subtle.digest("SHA-256", copy.buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_AVATARS)) {
        db.createObjectStore(STORE_AVATARS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_PROJECTS)) {
        db.createObjectStore(STORE_PROJECTS, { keyPath: "avatarId" });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB を開けませんでした"));
  });
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        let t: IDBTransaction;
        let req: IDBRequest<T>;
        try {
          t = db.transaction(store, mode);
          req = fn(t.objectStore(store));
        } catch (e) {
          db.close();
          reject(e instanceof Error ? e : new Error(String(e)));
          return;
        }
        // 書き込みはトランザクション完了（コミット）まで待ってから resolve する。
        // 読み取りも complete 時に resolve して挙動を揃える（結果は request から取る）。
        t.oncomplete = () => {
          db.close();
          resolve(req.result);
        };
        const fail = (): void => {
          db.close();
          reject(req.error ?? t.error ?? new Error("IndexedDB 操作に失敗しました"));
        };
        t.onerror = fail;
        t.onabort = fail;
      }),
  );
}

export async function putAvatar(a: StoredAvatar): Promise<void> {
  await tx(STORE_AVATARS, "readwrite", (s) => s.put(a));
}

export async function listAvatars(): Promise<StoredAvatar[]> {
  const all = await tx<StoredAvatar[]>(STORE_AVATARS, "readonly", (s) => s.getAll() as IDBRequest<StoredAvatar[]>);
  return all.sort((a, b) => b.savedAt.localeCompare(a.savedAt));
}

export async function getAvatar(id: string): Promise<StoredAvatar | undefined> {
  return tx<StoredAvatar | undefined>(STORE_AVATARS, "readonly", (s) => s.get(id) as IDBRequest<StoredAvatar | undefined>);
}

/** 同じ avatarUid を持つ保存済みアバター一覧（savedAt 降順） */
export async function listAvatarsByUid(avatarUid: string): Promise<StoredAvatar[]> {
  const all = await listAvatars();
  return all.filter((a) => a.avatarUid === avatarUid);
}

/** 同じ hash（zip の中身が完全一致）を持つ保存済みアバターを探す */
export async function findAvatarByHash(hash: string): Promise<StoredAvatar | undefined> {
  const all = await listAvatars();
  return all.find((a) => a.hash === hash);
}

export async function deleteAvatar(id: string): Promise<void> {
  await tx(STORE_AVATARS, "readwrite", (s) => s.delete(id));
  await tx(STORE_PROJECTS, "readwrite", (s) => s.delete(id));
}

export async function putProject(project: unknown & { avatarId: string }): Promise<void> {
  await tx(STORE_PROJECTS, "readwrite", (s) => s.put(project));
}

export async function getProject<T>(avatarId: string): Promise<T | undefined> {
  return tx<T | undefined>(STORE_PROJECTS, "readonly", (s) => s.get(avatarId) as IDBRequest<T | undefined>);
}

export async function setActiveAvatarId(id: string | null): Promise<void> {
  await tx(STORE_META, "readwrite", (s) => s.put(id, "activeAvatarId"));
}

export async function getActiveAvatarId(): Promise<string | null> {
  const v = await tx<string | null | undefined>(STORE_META, "readonly", (s) => s.get("activeAvatarId") as IDBRequest<string | null | undefined>);
  return v ?? null;
}

/** IndexedDB が使えるか（プライベートブラウジング等で失敗しうる） */
export async function isAvailable(): Promise<boolean> {
  try {
    const db = await openDb();
    db.close();
    return true;
  } catch {
    return false;
  }
}
